import {FC, ReactNode} from 'react'

import {useHomepageContent} from '@/content/homepage'
import {useTranslation} from '@/i18n'
import {HOVER_TRANSITION, SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {IconSize} from '@/theme/icons'
import {CONTAINER_PADDING, CONTAINER_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import Carousel from '../common/carousel/Carousel'
import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {QuoteIcon} from '../common/icons/QuoteIcon'

export const TrademarkBenefitsCarousel: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation()
  const {otherBenefits} = useHomepageContent().whyRegisterTm

  const pagination = otherBenefits.map(({title}) => t(title))

  const settings = {
    infinite: true,
    speed: SLIDER_TRANSITION,
    autoplay: true,
    autoplaySpeed: 8000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dots: true,
    appendDots: (dots: ReactNode) => (
      <div>
        <ul className="dots">{dots}</ul>
      </div>
    ),
    customPaging: (i: number) => (
      <button type="button">
        <Text
          variant={TextVariant.h5}
          element="span"
          className="text-block"
          fontWeight={TextFontWeight.bold}
        >
          {pagination[i]}
        </Text>
      </button>
    ),
  }

  return (
    <>
      <div className="trademark-benefits">
        <Carousel settings={settings}>
          {otherBenefits.map(({quote, author, role}) => (
            <div key={author} className="wrapper">
              <div className="quote-icon">
                <QuoteIcon size={IconSize.medium} />
              </div>
              <Text
                variant={TextVariant.text24}
                fontWeight={TextFontWeight.light}
                element="blockquote"
              >
                {t(quote)}
              </Text>
              <div className="author">
                <Text variant={TextVariant.h5} element="p">
                  {t(author)}
                </Text>
              </div>
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                {t(role)}
              </Text>
            </div>
          ))}
        </Carousel>
      </div>

      <style jsx>{`
        .trademark-benefits {
          margin-top: ${theme.spacing[7]};
        }
        .wrapper {
          background-color: ${theme.palette.background.default};
          border-radius: 16px;
          padding: ${theme.spacing[5]} ${theme.spacing[7]};
        }
        .quote-icon {
          margin-bottom: ${theme.spacing[4]};
        }
        .author {
          margin-top: ${theme.spacing[4]};
          margin-bottom: ${theme.spacing[1]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            padding: ${theme.spacing[5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .trademark-benefits {
            margin-top: ${theme.spacing[6]};
          }
          .wrapper {
            padding: ${theme.spacing[4]};
          }
          .quote-icon {
            margin-bottom: ${theme.spacing[3]};
          }
          .author {
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>

      {/* Please, use global styles only if you need to override CSS from external libraries */}
      <style jsx global>{`
        .trademark-benefits .slick-slider {
          display: flex;
          flex-direction: row-reverse;
          justify-content: space-between;
          max-width: inherit;
        }
        .trademark-benefits .slick-list {
          flex: 1 0 665px;
          max-width: 665px;
          height: 100%;
          margin-left: ${theme.spacing[7]};
          margin-right: -${theme.spacing[2]};
        }
        .trademark-benefits .slick-slide {
          padding: 0 ${theme.spacing[2]};
        }
        .trademark-benefits .slick-dots {
          margin-top: 0;
        }
        .trademark-benefits .dots li {
          display: block;
          width: inherit;
          height: inherit;
          text-align: left;
          cursor: default;
        }
        .trademark-benefits .dots li button {
          display: inline-block;
          width: auto;
          height: auto;
          text-align: left;
          padding: ${theme.spacing[2]} ${theme.spacing[3.5]};
          border-left: 4px solid ${theme.palette.butter[200]};
          cursor: pointer;
          transition: all ${HOVER_TRANSITION}ms ease-in-out;
        }
        .trademark-benefits .dots li.slick-active button {
          border-color: ${theme.palette.primary.main};
        }
        .trademark-benefits .dots li button:before {
          content: none;
        }
        .trademark-benefits .dots li button > * {
          color: ${theme.palette.text.light};
          transition: all ${HOVER_TRANSITION}ms ease-in-out;
        }
        .trademark-benefits .dots li.slick-active button > * {
          color: ${theme.palette.text.primary};
          font-weight: ${theme.fontWeight.semibold};
        }
        @media ${TABLET_SCREENSIZE} {
          .trademark-benefits .slick-slider {
            flex-direction: column-reverse;
            align-items: center;
            margin-left: -${CONTAINER_PADDING};
            margin-right: -${CONTAINER_PADDING};
          }
          .trademark-benefits .slick-list {
            width: 100%;
            max-width: 100%;
            flex: inherit;
            margin-top: ${theme.spacing[5]};
            margin-left: 0;
            margin-right: 0;
          }
          .trademark-benefits .slick-slide {
            padding: 0 ${theme.spacing[5]};
          }
          .trademark-benefits .slick-dots {
            height: 45px;
            overflow: hidden;
            margin-top: 0;
          }
          .trademark-benefits .dots {
            display: flex;
            width: 100%;
            height: 60px;
            overflow-x: auto;
            overflow-y: hidden;
            margin-top: 0;
          }
          .trademark-benefits .dots li:first-child {
            padding-left: ${CONTAINER_PADDING};
          }
          .trademark-benefits .dots li:last-child {
            padding-right: ${CONTAINER_PADDING};
          }
          .trademark-benefits .dots li button {
            width: 100%;
            padding: 0 ${theme.spacing[4]} ${theme.spacing[3]};
            border-left: 0;
            box-shadow: inset 0px -4px 0px 0px ${theme.palette.butter[200]};
            text-align: center;
            white-space: nowrap;
          }
          .trademark-benefits .dots li.slick-active button {
            box-shadow: inset 0px -4px 0px 0px ${theme.palette.primary.main};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .trademark-benefits .slick-slider {
            margin-left: -${CONTAINER_PADDING_MOBILE};
            margin-right: -${CONTAINER_PADDING_MOBILE};
          }
          .trademark-benefits .slick-list {
            margin-top: ${theme.spacing[3]};
            max-width: calc(100vw - ${theme.spacing[3]} * 2);
          }
          .trademark-benefits .slick-slide {
            padding: 0 ${theme.spacing[3]};
          }
          .trademark-benefits .dots li:first-child {
            padding-left: ${CONTAINER_PADDING_MOBILE};
          }
          .trademark-benefits .dots li:last-child {
            padding-right: ${CONTAINER_PADDING_MOBILE};
          }
          .trademark-benefits .dots li button {
            padding: 0 ${theme.spacing[3]} ${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
